import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiUserCheck, FiUserX, FiMail, FiKey, FiFilter } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

export default function Staff() {
  const { tenant } = useAuth();
  const [staff, setStaff] = useState([]);
  const [teachers, setTeachers] = useState([]); // 강사 선택을 위한 별도 목록
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [roleFilter, setRoleFilter] = useState('all'); // 'all', 'teacher', 'assistant'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'teacher',
    assignedTeachers: [] // 조교 전용: 담당 강사 ID 배열
  });

  // Pro 플랜 체크
  const isPro = tenant?.plan?.type === 'pro';
  const canUseAssistant = isPro;

  // 전화번호 포맷팅 (01012345678 → 010-1234-5678)
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const numbers = String(phone).replace(/[^0-9]/g, '');
    if (numbers.length === 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    if (numbers.length === 10) {
      if (numbers.startsWith('02')) {
        return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6)}`;
      }
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
    }
    return String(phone);
  };

  const loadData = useCallback(async () => {
    try {
      // role 필터에 따라 API 호출
      let roleParam = null;
      if (roleFilter === 'teacher') roleParam = 'teacher';
      else if (roleFilter === 'assistant') roleParam = 'assistant';

      const params = roleParam ? { role: roleParam } : {};

      const [staffRes, teachersRes, classesRes] = await Promise.all([
        api.get('/api/users', { params }),
        api.get('/api/users', { params: { role: 'teacher' } }), // 강사 목록 (조교 할당용)
        api.get('/api/class-groups')
      ]);

      // 필터링: teacher + assistant만
      const filteredStaff = (staffRes.data.users || []).filter(u =>
        u.role === 'teacher' || u.role === 'assistant'
      );

      setStaff(filteredStaff);
      setTeachers(teachersRes.data.users || []);
      setClasses(classesRes.data.classGroups || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, [roleFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreateModal = (role = 'teacher') => {
    // Pro 플랜이 아닌데 조교 생성 시도 시 경고
    if (role === 'assistant' && !canUseAssistant) {
      alert('조교 기능은 Pro 플랜부터 사용 가능합니다.\n플랜을 업그레이드해주세요.');
      return;
    }

    setEditingStaff(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      role,
      assignedTeachers: []
    });
    setShowModal(true);
  };

  const openEditModal = (staffMember) => {
    setEditingStaff(staffMember);
    setFormData({
      name: staffMember.name,
      email: staffMember.email,
      password: '',
      phone: staffMember.phone || '',
      role: staffMember.role,
      assignedTeachers: staffMember.assistantInfo?.assignedTeachers?.map(t => t._id || t) || []
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingStaff) {
        // Update
        const updateData = {
          name: formData.name,
          phone: formData.phone,
          role: formData.role
        };
        if (formData.password) updateData.password = formData.password;

        // 조교인 경우 assignedTeachers 업데이트
        if (formData.role === 'assistant') {
          updateData.assistantInfo = {
            assignedTeachers: formData.assignedTeachers
          };
        }

        await api.put(`/api/users/${editingStaff._id}`, updateData);
        alert('수정되었습니다');
      } else {
        // Create
        const createData = {
          ...formData,
          role: formData.role
        };

        // 조교인 경우 assistantInfo 추가
        if (formData.role === 'assistant') {
          createData.assistantInfo = {
            assignedTeachers: formData.assignedTeachers
          };
        }

        await api.post('/api/users', createData);
        alert(`${formData.role === 'teacher' ? '강사' : '조교'}가 생성되었습니다`);
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '처리에 실패했습니다');
    }
  };

  const handleDelete = async (staffMember) => {
    if (!window.confirm(`${staffMember.name} ${staffMember.role === 'teacher' ? '강사' : '조교'}를 완전히 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`)) return;

    try {
      await api.delete(`/api/users/${staffMember._id}?permanent=true`);
      alert('삭제되었습니다');
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '삭제에 실패했습니다');
    }
  };

  const toggleActive = async (staffMember) => {
    try {
      await api.put(`/api/users/${staffMember._id}`, {
        isActive: !staffMember.isActive
      });
      loadData();
    } catch (error) {
      alert('상태 변경에 실패했습니다');
    }
  };

  const getStaffClasses = (staffId) => {
    return classes.filter(c => c.teachers?.some(t =>
      (t._id || t) === staffId
    ));
  };

  // 조교의 담당 강사 이름 가져오기
  const getAssignedTeacherNames = (assistantInfo) => {
    if (!assistantInfo || !assistantInfo.assignedTeachers || assistantInfo.assignedTeachers.length === 0) {
      return '학원 전체';
    }
    return assistantInfo.assignedTeachers.map(t => {
      const teacher = teachers.find(teach => teach._id === (t._id || t));
      return teacher ? teacher.name : '알 수 없음';
    }).join(', ');
  };

  // Stats
  const stats = {
    total: staff.length,
    teachers: staff.filter(s => s.role === 'teacher').length,
    assistants: staff.filter(s => s.role === 'assistant').length,
    active: staff.filter(s => s.isActive).length
  };

  return (
    <div className="staff-page">
      <div className="page-header">
        <h2>직원 관리</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-primary" onClick={() => openCreateModal('teacher')}>
            <FiPlus /> 강사 추가
          </button>
          {canUseAssistant && (
            <button className="btn btn-secondary" onClick={() => openCreateModal('assistant')}>
              <FiPlus /> 조교 추가
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">전체 직원</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.teachers}</div>
          <div className="stat-label">강사</div>
        </div>
        {canUseAssistant && (
          <div className="stat-card">
            <div className="stat-value">{stats.assistants}</div>
            <div className="stat-label">조교</div>
          </div>
        )}
        <div className="stat-card">
          <div className="stat-value">{stats.active}</div>
          <div className="stat-label">활성</div>
        </div>
      </div>

      {/* Filter */}
      <div className="filter-bar" style={{ marginBottom: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
        <FiFilter />
        <button
          className={`btn ${roleFilter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setRoleFilter('all')}
          style={{ padding: '6px 12px' }}
        >
          전체
        </button>
        <button
          className={`btn ${roleFilter === 'teacher' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setRoleFilter('teacher')}
          style={{ padding: '6px 12px' }}
        >
          강사만
        </button>
        {canUseAssistant && (
          <button
            className={`btn ${roleFilter === 'assistant' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setRoleFilter('assistant')}
            style={{ padding: '6px 12px' }}
          >
            조교만
          </button>
        )}
      </div>

      {/* Staff List */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>구분</th>
              <th>이름</th>
              <th>이메일</th>
              <th>연락처</th>
              <th>담당/소속</th>
              <th>상태</th>
              <th>가입일</th>
              <th>액션</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="loading">로딩 중...</td></tr>
            ) : staff.length === 0 ? (
              <tr><td colSpan="8" className="empty">등록된 직원이 없습니다</td></tr>
            ) : (
              staff.map((member) => {
                const memberClasses = getStaffClasses(member._id);
                return (
                  <tr key={member._id}>
                    <td>
                      <span className={`badge ${member.role === 'teacher' ? 'primary' : 'secondary'}`}>
                        {member.role === 'teacher' ? '강사' : '조교'}
                      </span>
                    </td>
                    <td>
                      <div className="staff-name">
                        <FiUserCheck className="icon" />
                        {member.name}
                      </div>
                    </td>
                    <td>{member.email}</td>
                    <td>{member.phone || '-'}</td>
                    <td>
                      {member.role === 'teacher' ? (
                        memberClasses.length > 0 ? (
                          <div className="class-tags">
                            {memberClasses.map(c => (
                              <span key={c._id} className="badge">{c.name}</span>
                            ))}
                          </div>
                        ) : '-'
                      ) : (
                        <small style={{ color: '#6B7280' }}>
                          {getAssignedTeacherNames(member.assistantInfo)}
                        </small>
                      )}
                    </td>
                    <td>
                      <span className={`status ${member.isActive ? 'active' : 'inactive'}`}>
                        {member.isActive ? '활성' : '비활성'}
                      </span>
                    </td>
                    <td>{format(new Date(member.createdAt), 'yyyy-MM-dd')}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="icon-btn" onClick={() => openEditModal(member)} title="수정">
                          <FiEdit2 />
                        </button>
                        <button
                          className={`icon-btn ${member.isActive ? 'warning' : 'success'}`}
                          onClick={() => toggleActive(member)}
                          title={member.isActive ? '비활성화' : '활성화'}
                        >
                          {member.isActive ? <FiUserX /> : <FiUserCheck />}
                        </button>
                        <button className="icon-btn danger" onClick={() => handleDelete(member)} title="삭제">
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingStaff ? `${editingStaff.role === 'teacher' ? '강사' : '조교'} 수정` : `${formData.role === 'teacher' ? '강사' : '조교'} 추가`}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              {/* 신규 생성 시 역할 선택 */}
              {!editingStaff && (
                <div className="form-group">
                  <label>역할 *</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    required
                  >
                    <option value="teacher">강사</option>
                    {canUseAssistant && <option value="assistant">조교</option>}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>이름 *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="이름"
                  required
                />
              </div>
              <div className="form-group">
                <label>이메일 *</label>
                <div className="input-with-icon">
                  <FiMail className="input-icon" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="example@email.com"
                    required
                    disabled={!!editingStaff}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>{editingStaff ? '비밀번호 (변경시에만 입력)' : '비밀번호 *'}</label>
                <div className="input-with-icon">
                  <FiKey className="input-icon" />
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editingStaff ? '변경시에만 입력' : '비밀번호'}
                    required={!editingStaff}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>연락처</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: formatPhoneNumber(e.target.value) })}
                  placeholder="010-0000-0000"
                />
              </div>

              {/* 조교 전용: 담당 강사 선택 */}
              {formData.role === 'assistant' && (
                <div className="form-group">
                  <label>담당 강사 (선택)</label>
                  <select
                    multiple
                    value={formData.assignedTeachers}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setFormData({ ...formData, assignedTeachers: selected });
                    }}
                    style={{ minHeight: '120px' }}
                  >
                    {teachers.map(teacher => (
                      <option key={teacher._id} value={teacher._id}>
                        {teacher.name}
                      </option>
                    ))}
                  </select>
                  <small className="text-muted">
                    * 미선택 시 학원 전체 조교로 등록됩니다
                    <br />
                    * Ctrl/Cmd + 클릭으로 다중 선택 가능
                  </small>
                </div>
              )}

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingStaff ? '수정' : '추가'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
